import React, { type MouseEventHandler, type DetailedHTMLProps, forwardRef, useRef, useImperativeHandle, HTMLAttributes } from "react";
import { twMerge, twJoin } from "tailwind-merge";

import SupDepositAdd from "../../../../icons/DepositAdd";
import SupDepositRemove from "../../../../icons/DepositRemove";

import { useTextAligner } from "../hooks/useTextAligner";

export interface RemoveButtonProps extends DetailedHTMLProps<HTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
  hovered?: boolean;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  textCenter?: boolean;
  delayAnimation?: boolean;
}

export interface RemoveButtonInstance {
  getWidth: () => number;
}

const PADDING_X = 16;
const REMOVE_BUTTON_OFFSET = "--remove-button-offset";

export const RemoveButton = forwardRef<RemoveButtonInstance, RemoveButtonProps>(({ textCenter = false, className, disabled, onClick, style, ...restProps }, ref) => {
  const firstContentRef = useRef<HTMLLIElement>(null);
  const secondContentRef = useRef<HTMLLIElement>(null);
  const { buttonProps, contentProps } = useTextAligner(textCenter, REMOVE_BUTTON_OFFSET);

  useImperativeHandle(
    ref,
    () => ({
      getWidth: () => {
        const contentWidth = Math.max(firstContentRef.current?.offsetWidth ?? 0, secondContentRef.current?.offsetWidth ?? 0);
        return contentWidth + PADDING_X;
      },
    }),
    []
  );

  return (
    <button
      {...restProps}
      {...buttonProps}
      disabled={disabled}
      className={twMerge(
        "remove-button",
        "removeButton",
        "h-10 font-mono tracking-normal uppercase text-left bg-primary-200 hover:bg-primary-50 transition-all duration-300",
        className
      )}
      onClick={onClick}
      style={style}
    >
      <span {...contentProps} className="inline-flex h-full transition-transform duration-300">
        <ul className={twJoin("inline-flex flex-col h-full", !textCenter && "px-2")}>
          <li ref={firstContentRef} className={twJoin("inline-flex shrink-0 h-10 items-center gap-2 text-primary-900 ", textCenter && "justify-center")}>
            <SupDepositAdd width={24} height={24} />
            Added
          </li>
          <li ref={secondContentRef} className={twJoin("inline-flex shrink-0 h-10 items-center gap-2 text-primary-900", textCenter && "justify-center")}>
            <SupDepositRemove width={24} height={24} />
            Remove
          </li>
        </ul>
      </span>
    </button>
  );
});

RemoveButton.displayName = "RemoveButton";
